import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ConfigPage from './pages/ConfigPage';
import PanelFromText from './pages/PanelFromText';
import MatrixPage from './pages/MatrixPage';
import ProductsLibrary from './pages/ProductsLibrary';
import ProductKnowledge from './pages/ProductKnowledge';
import AiPrompts from './pages/AiPrompts';
import TraceBuilder from './pages/TraceBuilder';
import DocImport from './pages/DocImport';
import TraceBaselinesPage from './pages/TraceBaselinesPage';
import TrainingConfig from './pages/TrainingConfig';
import TrainingPanel from './pages/TrainingPanel';
import PeoplePanel from './pages/PeoplePanel';
import PeopleConfig from './pages/PeopleConfig';
import ReportPanel from './pages/ReportPanel';
import QuizList from './pages/QuizList';
import QuizBuilder from './pages/QuizBuilder';
import MyTraining from './pages/MyTraining';
import TakeQuiz from './pages/TakeQuiz';
import ReviewHome from './pages/review/ReviewHome';
import RiskHome from './pages/risk/RiskHome';
import RiskDetailPage from './pages/risk/RiskDetailPage';
import BaselinesPage from './pages/risk/BaselinesPage';
import ImportPage, { ImportUpload } from './pages/risk/ImportPage';
import StudioPage from './pages/risk/StudioPage';
import ReportsPage from './pages/risk/ReportsPage';
import DhfHome from './pages/dhf/DhfHome';
import DhfPage from './pages/dhf/DhfPage';
import DhfDocumentPage from './pages/dhf/DhfDocumentPage';
import DhfBaselinesPage from './pages/dhf/DhfBaselinesPage';
import DhfVariantsPage from './pages/dhf/DhfVariantsPage';
import DhfVariantPage from './pages/dhf/DhfVariantPage';
import DhfVariantComparePage from './pages/dhf/DhfVariantComparePage';
import DhfVariantPullPage from './pages/dhf/DhfVariantPullPage';
import DhfVariantMergePage from './pages/dhf/DhfVariantMergePage';
import ReviewItemPage from './pages/review/ReviewItemPage';
import ReviewDocPage from './pages/review/ReviewDocPage';
import ReviewOpenPage from './pages/review/ReviewOpenPage';
import ReviewBell from './components/ReviewBell';
import UserMenu from './components/UserMenu';
import NewVersionBar from './components/NewVersionBar';
import ReleaseNotesModal from './components/ReleaseNotesModal';
import AppNav, { ShellCrumb, OrcanosLogo } from './components/AppNav';
import BackgroundExports from './bom/tree/BackgroundExports';
import BomViewer, { BOM_VIEWS } from './bom/BomViewer';
import SbomViewer, { SBOM_VIEWS } from './sbom/SbomViewer';

import ProductStructures from './bom/structures/ProductStructures';
import IntegrationsHome from './pages/integrations/IntegrationsHome';
import IntegrationBuilder from './pages/integrations/IntegrationBuilder';
import IntegrationRunLog from './pages/integrations/RunLog';
import {
  bomViewsFor,
  loadSettings as loadBomSettings,
  SETTINGS_EVENT as BOM_SETTINGS_EVENT,
} from './bom/settings/settingsStore';
import AdminSettings from './pages/AdminSettings';
import Preferences from './pages/Preferences';
import SessionExpiryModal from './components/SessionExpiryModal';
import AiDisclaimerModal from './components/AiDisclaimerModal';
import PresenceRail from './components/PresenceRail';
import InviteButton from './components/InviteButton';
import ChatDock from './components/ChatDock';
import useSessionExpiry from './hooks/useSessionExpiry';
import useCollab from './hooks/useCollab';
import { setCollabWhere } from './utils/collab';
import './styles/Collab.css';
import { MODULES, moduleByKey, moduleOf } from './modules';
import { bootIntercom, updateIntercomUser } from './intercom';
import './App.css';

// Left-nav icon per BOM / Software BOM view (a `KindIcon` kind, bom/tree/icons.jsx).
const NAV_ICON = {
  'ps-dashboard': 'dash', 'ps-structures': 'tree', boms: 'bom', assemblies: 'asm', parts: 'part', dms: 'doc', ecos: 'eco',
  'ps-parts': 'usage', 'ps-baselines': 'baseline', 'ps-branches': 'branch', 'ps-import': 'import',
  'sw-dashboard': 'dash', 'sw-releases': 'release', 'sw-components': 'component', 'sw-vulns': 'vuln',
  'sw-studio': 'ai', 'sw-import': 'import',
};

/**
 * The ONE writer of "where this tab is looking".
 *
 * Presence shows a colleague what you are on, so something has to report it. Doing that
 * from each page meant several writers racing on unmount — a page clearing the value as
 * the next one set it left everybody looking at a blank line. So it is derived here, from
 * the route alone, and no page touches it.
 *
 * It reports IDs, never a title: the "viewing …" line a colleague sees is resolved on the
 * server under THEIR privacy scope, so a private panel's name never leaves this tab
 * (Critical Note #71).
 */
function CollabLocation() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname || '';
    const panel = path.match(/^\/(?:trace|training)\/(?:matrix|report|panel|build)\/([^/?#]+)/);
    setCollabWhere(moduleOf(path) || '', panel ? panel[1] : '');
  }, [location.pathname]);
  return null;
}

/**
 * Redirect a legacy `/<thing>/<rest>` URL to `<to>/<rest>`, preserving whatever followed.
 *
 * `<Navigate to="...">` alone can't do this — a splat route's matched tail is only readable
 * from inside a rendered element, so the redirect needs a component to read `params['*']`.
 */
function LegacyRedirect({ to }) {
  const params = useParams();
  const rest = params['*'] || '';
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${rest ? `/${rest}` : ''}${search}${hash}`} replace />;
}

/**
 * Send an unauthenticated visitor to /login **keeping the query string**.
 *
 * ⚠️ `<Navigate to="/login" />` silently drops `?...`, and that is not cosmetic.
 * The region hand-off arrives as `/?rr=1&u=<orcanos url>`: `u` prefills the URL
 * field so nobody types it twice, and `rr` is the loop guard. Losing both meant
 * the landing region fell back to its DEFAULT account, decided that account
 * belonged to the other region, and — with the guard gone too — bounced straight
 * back. The visible symptom was a page that "refreshed and went back to the
 * previous URL", which is precisely what it did: US → EU → US → stop, landing on
 * the account you started from.
 */
function LoginRedirect() {
  const { pathname, search, hash } = useLocation();
  // The Orcanos form's "Start Review" button (browser extension) lands on /review/open — keep
  // it across the sign-in so the person ends up on the review they asked for, not the home page.
  if (pathname.startsWith(RETURN_PATH_PREFIX)) {
    try { sessionStorage.setItem(RETURN_KEY, `${pathname}${search}`); } catch (_) { /* private mode */ }
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/login${search}${hash}`} replace />;
}

const RETURN_KEY = 'tm.returnTo';
const RETURN_PATH_PREFIX = '/review/open';

/** After sign-in: back to the deep link LoginRedirect kept, once; otherwise an import left open
 * at sign-out (same person only); otherwise the home module. */
function AfterLogin({ homeRoute, ready, who }) {
  // Wait for /api/auth/status: until it lands the module list is the fallback, which leaves
  // out opt-in Doc Review, and /review/open would bounce home with the deep link spent.
  if (!ready) return null;
  let back = '';
  try { back = sessionStorage.getItem(RETURN_KEY) || ''; sessionStorage.removeItem(RETURN_KEY); } catch (_) { /* */ }
  if (back.startsWith(RETURN_PATH_PREFIX)) return <Navigate to={back} replace />;
  const resume = readResume();
  if (resume && resume.who === who && resume.path.startsWith(RESUME_PATH_PREFIX)) {
    return <Navigate to={resume.path} replace />;
  }
  return <Navigate to={homeRoute} replace />;
}

/*
 * A risk import is long, multi-step work: an import that was OPEN when the person signed out
 * (or timed out) is where they land after signing back in. localStorage, so it survives the
 * tab too; keyed to the identity, so another person on this browser is never sent into it.
 * Leaving the import for any other page in the app clears it — a sign-out does not, because
 * the signed-in tree (and this tracker) is gone before the route changes.
 */
const RESUME_KEY = 'tm.risk.importResume';
const RESUME_PATH_PREFIX = '/risk/import/';
function readResume() {
  try {
    const r = JSON.parse(localStorage.getItem(RESUME_KEY) || 'null');
    return r && typeof r.path === 'string' ? r : null;
  } catch (_) { return null; }
}

function ImportResumeTracker({ who }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!who || pathname === '/login') return;
    try {
      if (pathname.startsWith(RESUME_PATH_PREFIX)) localStorage.setItem(RESUME_KEY, JSON.stringify({ path: pathname, who }));
      else localStorage.removeItem(RESUME_KEY);
    } catch (_) { /* private mode */ }
  }, [pathname, who]);
  return null;
}

/**
 * Who this tab believes it is talking to. A browser profile carries ONE `session_id` cookie, so
 * signing into a second account anywhere in it re-points EVERY open tab — this is the value that
 * changes underneath a tab that was left open. See Critical Note #42.
 */
function identityOf(status) {
  return `${status?.account_name || ''}|${status?.user_id || ''}`;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releaseNotes, setReleaseNotes] = useState(null);
  const [barNotesOpen, setBarNotesOpen] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  // Message the login screen shows after an automatic sign-out; '' for a normal logout.
  const [loginNotice, setLoginNotice] = useState('');
  // The account+user this tab loaded as, and the mismatch that ended it. Once `staleSession`
  // is set the tab is frozen: anything it saved from here would be filed under the OTHER
  // account, which is the whole point of the guard.
  const identityRef = useRef(null);
  const [staleSession, setStaleSession] = useState(null);
  // null until the first fetch resolves — never treated as "not accepted" in the meantime,
  // so the gate can't flash before we actually know. { accepted, current_version,
  // current_markdown, history } once loaded (see ai_disclaimer_api.status).
  const [aiDisclaimer, setAiDisclaimer] = useState(null);
  const [aiDisclaimerViewerOpen, setAiDisclaimerViewerOpen] = useState(false);
  // Which disclaimer version's "was updated" toast the user has already dismissed or viewed,
  // so a non-material text edit doesn't nag on every reload. Per-browser only.
  const [disclaimerNoticeDismissedVersion, setDisclaimerNoticeDismissedVersion] = useState(() => {
    try { return localStorage.getItem('aiDisclaimerNoticeSeenVersion') || ''; } catch { return ''; }
  });

  useEffect(() => {
    checkAuth();
    fetchReleaseNotes();
    fetchAppSettings();
  }, []);

  // Intercom chat widget — boots anonymously so "Let's Chat" works on the login screen too.
  useEffect(() => {
    bootIntercom(user);
  }, []);
  useEffect(() => {
    updateIntercomUser(user);
  }, [user]);

  // Does the live session still belong to the account this tab rendered as? Fed by the
  // expiry hook's existing /status resync (30 s + on tab-visible) rather than a second poll
  // of its own — the check has to live in exactly one place, and that poll already runs at
  // the moment that matters: the user coming back to a tab they left open. A failed or 401
  // status never reaches here, so a network blip can't raise a false "you switched account".
  const noteIdentity = (status) => {
    const now = identityOf(status);
    if (!identityRef.current || now === identityRef.current) return;
    setStaleSession((prev) => prev || { was: identityRef.current, now });
  };

  const fetchAppSettings = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/app-settings.json`);
      if (response.ok) setAppSettings(await response.json());
    } catch (error) {
      console.error('Failed to load app-settings.json:', error);
    }
  };

  const fetchReleaseNotes = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/release_notes.json`);
      if (response.ok) {
        const data = await response.json();
        setReleaseNotes(data);
      }
    } catch (error) {
      console.error('Failed to load release notes:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/status');
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setIsAuthenticated(true);
        // The identity this tab is now bound to. Re-stamped on every explicit auth check
        // (mount, and after a login in THIS tab) — never by the background verifier, which
        // must compare against the identity the tab actually rendered as.
        identityRef.current = identityOf(data);
        setStaleSession(null);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // `notice` is set only by the automatic sign-out — the login screen has to say WHY the
  // user is looking at it, or an expiry is indistinguishable from the app logging them out
  // at random. A click on Sign Out passes a click event here, not an options object, hence
  // the shape check.
  const handleLogout = async (opts) => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      // Already gone server-side, or offline — the client state below is what matters.
    }
    setIsAuthenticated(false);
    setUser(null);
    setLoginNotice((opts && typeof opts === 'object' && typeof opts.notice === 'string')
      ? opts.notice : '');
  };

  // Automatic sign-out on the session lifetime Orcanos reports at login (see the hook).
  // A STABLE sign-out for the BOM module. This component re-renders every second (the
  // session countdown below), and an inline arrow here gave BOM a new callback each tick —
  // which re-fetched its whole grid once a second. The ref keeps it pointing at the latest
  // handleLogout without changing identity.
  const handleLogoutRef = useRef(handleLogout);
  handleLogoutRef.current = handleLogout;
  const handleBomSessionExpired = useCallback(
    () => handleLogoutRef.current({ notice: 'Your session expired. Please sign in again.' }),
    []
  );

  // BOM's nav sub-items depend on its settings: "Parts - DMS items" and the ECO list appear
  // only once their filters are set. Read once per sign-in, then kept current by the store's
  // event (fired by the module page and by Admin → Settings on save). A failed read keeps the
  // two fixed views — the module page shows the real error.
  const [bomNavViews, setBomNavViews] = useState(BOM_VIEWS);
  // Only the HARDWARE part reads these settings; a Software-BOM-only tenant would get a 403.
  const bomLicensed = isAuthenticated && Array.isArray(user?.modules) && user.modules.includes('bom')
    && (!Array.isArray(user?.bom_parts) || !user.bom_parts.length || user.bom_parts.includes('hardware'));
  useEffect(() => {
    const onChange = (e) => setBomNavViews(bomViewsFor(e.detail));
    window.addEventListener(BOM_SETTINGS_EVENT, onChange);
    if (bomLicensed) loadBomSettings().catch(() => {});
    return () => window.removeEventListener(BOM_SETTINGS_EVENT, onChange);
  }, [bomLicensed]);

  const { secondsLeft, warn, stayAlive } = useSessionExpiry({
    enabled: isAuthenticated,
    onExpire: () => handleLogout({
      notice: 'Your session expired after a period of inactivity. Please sign in again.',
    }),
    onIdentity: noteIdentity,
  });

  // Presence and chat. ONE poll for the whole app, mounted here because the rail lives in
  // the header and the dock floats over every route. Its effect takes no dependencies —
  // this component re-renders every second (Critical Note #59) and a poll that restarted
  // with it would fire sixty times a minute instead of twelve.
  const collab = useCollab({ enabled: isAuthenticated });
  const collabPerson = collab.openWith
    ? collab.people.find((p) => p.user_id === collab.openWith.userId)
      || { user_id: collab.openWith.userId, display_name: collab.openWith.userId,
           initials: collab.openWith.userId.slice(0, 2).toUpperCase(), online: false, where: '' }
    : null;

  // AI Usage Disclaimer status — fetched whenever the tenant's AI licence is on. Mirrors the
  // `aiEnabled` derivation below without depending on it: that const isn't computed until
  // after the early `loading` return just below, and hooks can't follow a conditional return.
  const fetchAiDisclaimerStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-disclaimer/status');
      if (response.ok) setAiDisclaimer(await response.json());
    } catch (error) {
      console.error('Failed to load AI disclaimer status:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.ai_enabled !== false) {
      fetchAiDisclaimerStatus();
    }
  }, [isAuthenticated, user?.ai_enabled, fetchAiDisclaimerStatus]);

  if (loading) return <div className="loading">Loading...</div>;

  // Account-level AI licence, from /api/auth/status. Default true so a slow/failed status
  // fetch never hides a feature the account is entitled to; the backend 403s regardless.
  const aiEnabled = user?.ai_enabled !== false;

  // A non-material disclaimer edit (current_version moved but the tenant's existing
  // acceptance is still valid — see ai_disclaimer_api._status_for) surfaces as a dismissible
  // toast instead of re-gating.
  const latestAcceptedDisclaimerVersion = aiDisclaimer?.history?.length
    ? aiDisclaimer.history[aiDisclaimer.history.length - 1].version
    : null;
  const disclaimerWasUpdated = !!(
    aiDisclaimer?.accepted &&
    aiDisclaimer?.current_version &&
    latestAcceptedDisclaimerVersion &&
    latestAcceptedDisclaimerVersion !== aiDisclaimer.current_version &&
    disclaimerNoticeDismissedVersion !== aiDisclaimer.current_version
  );
  const dismissDisclaimerNotice = () => {
    if (!aiDisclaimer?.current_version) return;
    try { localStorage.setItem('aiDisclaimerNoticeSeenVersion', aiDisclaimer.current_version); } catch { /* ignore */ }
    setDisclaimerNoticeDismissedVersion(aiDisclaimer.current_version);
  };

  // Orcanos admin flag (from /api/auth/status). Gates WRITES in the Product Knowledge /
  // AI-prompt module, and is the QA-MANAGER gate in the Training module: an admin authors
  // quizzes, everyone else (viewers included) is a trainee. Fails closed: only an explicit
  // true unlocks it, and a session predating the flag reads false until the next login
  // (the backend re-checks and 403s regardless).
  const isAdmin = user?.is_admin === true;

  // May create/edit/delete panels (Orcanos dashboard-add right, from /api/auth/status).
  // Fails OPEN like the permission letters — only an explicit false locks the UI down, and
  // the backend 403s regardless. A read-only (viewer) licence reports false.
  const canManagePanels = user?.can_manage_panels !== false;

  // A READ-ONLY (viewer) licence: Orcanos denies BOTH authoring axes — project data
  // (`Item_type[].Permission` is "V" everywhere ⇒ `can_add` false) and dashboards
  // (`Enable_add_dashboard` false). Critical Note #23 keeps those two axes separate,
  // and this is the one place they are read together, deliberately requiring both:
  //
  //   * Both are FAIL-OPEN individually (unknown ⇒ allowed), so an unreadable
  //     permission string can never demote a real user to viewer.
  //   * An Orcanos admin is never a viewer, whatever the letters say.
  //
  // What it changes is the Training module's front door and NOTHING else: a viewer
  // is a trainee, so their home is their own to-do rather than a compliance panel
  // built for the person auditing them. It is a ROUTING decision, not a permission —
  // the report API is unchanged, because a read-only QA reviewer who is handed a
  // panel link must still be able to open it.
  const isViewer = !isAdmin && user?.can_add === false && user?.can_manage_panels === false;

  // Which modules this ACCOUNT is licensed for, from /api/auth/status. Defaults to every
  // module that is on by default, so a slow or failed status fetch can never blank the app;
  // the backend gates the data regardless. An OPT-IN module (BOM) is never in that fallback —
  // a status hiccup must not show a module the account was never given.
  //
  // Filtered to modules this build KNOWS before the emptiness check: "/" now redirects to the
  // first licensed module, and a list of only unknown keys would otherwise send it to a
  // module the account doesn't have, whose route redirects back to "/" — forever.
  const knownModules = Array.isArray(user?.modules)
    ? user.modules.filter((k) => moduleByKey(k))
    : [];
  const modules = knownModules.length
    ? knownModules
    : MODULES.filter((m) => !m.optIn).map((m) => m.key);
  const hasTrace = modules.includes('trace');
  const hasTraining = modules.includes('training');
  const hasBom = modules.includes('bom');
  // The BOM module has two parts (v4.30.0): the hardware BOM and the Software BOM, each with its
  // own licence. A backend that does not report `bom_parts` predates the split: hardware only.
  const bomParts = Array.isArray(user?.bom_parts) && user.bom_parts.length ? user.bom_parts : ['hardware'];
  const hasHardwareBom = hasBom && bomParts.includes('hardware');
  const hasSbom = hasBom && bomParts.includes('sbom');
  // The first BOM menu entry — the Product structures dashboard when that is on (BOMs leaves the menu then).
  const bomHome = hasHardwareBom ? `/bom/${bomNavViews[0]?.key || 'boms'}` : '/bom/sw-releases';
  const hasReview = modules.includes('review');
  const hasRisk = modules.includes('risk');
  const hasIntegrations = modules.includes('integrations');
  const hasDhf = modules.includes('dhf');
  const soleModule = modules.length === 1 ? moduleByKey(modules[0]) : null;
  // Where "/" goes, and where a route the user may not reach falls back to: the first
  // licensed module, in nav order. There is no launcher page any more — the left nav is the
  // launcher, and it is on every screen.
  const firstModule = MODULES.find((m) => modules.includes(m.key)) || MODULES[0];
  const homeRoute = firstModule.home;
  // Inside the trace module, a refused route drops to that module's dashboard rather than all
  // the way out to the portal — losing your place is worse than being told no.
  const traceHome = hasTrace ? '/trace/dashboard' : homeRoute;
  const trainingHome = hasTraining ? '/training' : homeRoute;

  // Ask Paul — a separate Orcanos app, opened by silent login. Shown in BOTH modules (it is
  // an app, not a module route), and only when the backend says it can actually work: the
  // user's Orcanos 'O' right plus the handoff being configured on this server. Fails CLOSED,
  // unlike the permission gates around it — an unconfigured deployment offering a link that
  // always errors is worse than no link at all, and there is nothing to fail open *to*.
  const askPaulEnabled = user?.ask_paul_enabled === true;

  // The left nav is always shown (2026-10-27): it lists every module, unlicensed ones locked
  // with a "contact Orcanos" card, so there is always something to show.
  const showNav = true;
  const navSubItems = {
    // The Software BOM is its own root nav entry (key 'sbom'), though it lives under /bom.
    // Each sub-item carries the BOM module's kind icon: a work-item list shows that work
    // item's icon (the same one its rows show), any other page its own icon.
    bom: (hasHardwareBom ? bomNavViews : []).map((v) => ({ to: `/bom/${v.key}`, label: v.label, icon: NAV_ICON[v.key] })),
    sbom: (hasSbom ? SBOM_VIEWS : []).map((v) => ({ to: `/bom/${v.key}`, label: v.label, icon: NAV_ICON[v.key] })),
  };
  // Doc Review's "waiting for you" count, from the collab poll — the nav badge (D9).
  const navCounts = { review: hasReview ? (collab.review?.waiting || 0) : 0 };

  // The top bar. With the left nav it sits BESIDE the nav (as in Ask Paul) and shows a
  // breadcrumb, because the product name is in the nav's brand block. Without the nav it spans
  // the page and carries the logo and a module-named title, as it always has.
  const headerEl = isAuthenticated && (
          <header className={`header${showNav ? ' header--with-nav' : ''}`}>
            {showNav ? (
              <ShellCrumb accountName={user?.account_name} />
            ) : (
              <h1 className="app-title">
                <OrcanosLogo />
                <span>
                  {`Orcanos ${soleModule?.name || 'Traceability'}`}
                  {user?.account_name ? ` – ${user.account_name}` : ''}
                </span>
              </h1>
            )}
            <div className="header-right">
              {/* No "Welcome, <login>" here any more. The name belongs to the avatar — the
                  user menu now shows the person's real display name and email — and the
                  header's right edge is where the PEOPLE go. A login string repeated beside
                  a row of colleagues was the noisiest thing on the screen. */}
              {/* Renders nothing unless the deployment can actually send mail — the
                  component asks the server itself, so nothing here has to know. */}
              <InviteButton enabled={isAuthenticated} />
              {/* Doc Review notifications. Renders nothing unless the poll says the module is
                  licensed (`collab.review` is null otherwise). */}
              {hasReview && <ReviewBell counts={collab.review} />}
              {collab.enabled && (
                <PresenceRail people={collab.people} onOpen={collab.open} />
              )}
              {appSettings.helpUrl && (
                <a
                  href={appSettings.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-icon"
                  title="Open User Manual"
                  aria-label="Open User Manual"
                >
                  ?
                </a>
              )}
              <UserMenu
                userId={user?.user_id}
                displayName={collab.me?.display_name}
                email={collab.me?.email}
                onLogout={handleLogout}
                releaseNotes={releaseNotes}
                isAdmin={isAdmin}
                aiEnabled={aiEnabled}
                onOpenAiDisclaimer={() => setAiDisclaimerViewerOpen(true)}
              />
            </div>
          </header>
  );

  return (
    <Router basename={process.env.PUBLIC_URL || ""}>
      <div className="app">
        {isAuthenticated && (
          <NewVersionBar
            version={releaseNotes?.releases?.[0]?.version}
            onOpenReleaseNotes={() => setBarNotesOpen(true)}
          />
        )}
        {barNotesOpen && (
          <ReleaseNotesModal releases={releaseNotes?.releases} onClose={() => setBarNotesOpen(false)} />
        )}
        {!showNav && headerEl}

        {/* Another account signed in on this browser profile, so this tab's cookie no longer
            belongs to what it is showing. Freeze it: a save from here would be filed under the
            other account. Reload (not auto-reload) — the user may still want to read what is
            on screen, and losing their place without warning is its own bug. Note #42. */}
        {staleSession && (
          <div className="stale-session-overlay" role="alertdialog" aria-modal="true"
               aria-labelledby="stale-session-title">
            <div className="stale-session-box">
              <h2 id="stale-session-title">You signed in as a different account</h2>
              <p>
                This tab was open as <strong>{staleSession.was.split('|')[0] || 'another account'}</strong>
                {staleSession.was.split('|')[1] ? ` (${staleSession.was.split('|')[1]})` : ''},
                but this browser is now signed in as{' '}
                <strong>{staleSession.now.split('|')[0] || 'another account'}</strong>
                {staleSession.now.split('|')[1] ? ` (${staleSession.now.split('|')[1]})` : ''}.
              </p>
              <p className="stale-session-note">
                A browser can only hold one session at a time. Anything saved from this tab would
                go to the account that is signed in now — so it has been locked. Reload to
                continue as the current account, or open the other account in a separate browser
                profile or a private window.
              </p>
              <button className="btn-primary" onClick={() => window.location.reload()}>
                Reload this tab
              </button>
            </div>
          </div>
        )}

        {isAuthenticated && warn && (
          <SessionExpiryModal
            secondsLeft={secondsLeft}
            onStay={stayAlive}
            onLogout={() => handleLogout()}
          />
        )}

        {isAuthenticated && aiEnabled && aiDisclaimer && aiDisclaimer.accepted === false && (
          <AiDisclaimerModal
            mode="gate"
            markdown={aiDisclaimer.current_markdown}
            onAccepted={setAiDisclaimer}
          />
        )}

        {isAuthenticated && disclaimerWasUpdated && (
          <div className="ai-disclaimer-update-toast">
            <span>The AI Usage Disclaimer was updated (v{aiDisclaimer.current_version}).</span>
            <button onClick={() => setAiDisclaimerViewerOpen(true)}>View</button>
            <button onClick={dismissDisclaimerNotice} aria-label="Dismiss">✕</button>
          </div>
        )}

        {aiDisclaimerViewerOpen && (
          <AiDisclaimerModal
            mode="view"
            markdown={aiDisclaimer?.current_markdown}
            history={aiDisclaimer?.history}
            onClose={() => { setAiDisclaimerViewerOpen(false); dismissDisclaimerNotice(); }}
          />
        )}

        <div className="app-body">
        {isAuthenticated && showNav && (
          <AppNav modules={modules} bomParts={hasBom ? bomParts : []} connectors={Array.isArray(user?.connectors) ? user.connectors : []} askPaul={askPaulEnabled} knowledge={hasTrace && aiEnabled} subItems={navSubItems} counts={navCounts} />
        )}
        <div className="app-content">
        <CollabLocation />
        {isAuthenticated && user && <ImportResumeTracker who={identityOf(user)} />}
        {isAuthenticated && <BackgroundExports />}
        {showNav && headerEl}
        {/* Only the page scrolls — the version bar and the header above it stay put. A page's
            sticky toolbar (top: 0) sticks to the top of THIS box, just under the header. */}
        <div className="app-scroll">
        <Routes>
          {!isAuthenticated ? (
            <>
              <Route path="/login" element={<LoginPage onLogin={() => { setLoginNotice(''); setIsAuthenticated(true); checkAuth(); }} version={releaseNotes?.releases?.[0]?.version} notice={loginNotice} />} />
              <Route path="*" element={<LoginRedirect />} />
            </>
          ) : (
            <>
              {/* No launcher page: "/" opens the first licensed module, and the left nav
                  moves between them from anywhere. */}
              <Route path="/" element={<Navigate to={homeRoute} replace />} />

              {/* ---- Traceability module -------------------------------------------- */}
              <Route path="/trace" element={<Navigate to={traceHome} replace />} />
              <Route
                path="/trace/dashboard"
                element={hasTrace
                  ? <Dashboard module="trace" onLogout={handleLogout} aiEnabled={aiEnabled} knowledgeInNav={showNav} canManagePanels={canManagePanels} isAdmin={isAdmin} version={releaseNotes?.releases?.[0]?.version} account={user?.account_name} />
                  : <Navigate to={homeRoute} replace />}
              />
              {/* The wizard is a write flow — a user without the dashboard-add right can't
                  save from it, so the route is removed rather than left to 403 on Save. */}
              <Route path="/trace/config" element={hasTrace && canManagePanels ? <ConfigPage /> : <Navigate to={traceHome} replace />} />
              {/* Describe-a-panel is an AI feature: the route itself is gone for an account
                  without an AI licence, so a bookmarked URL can't reach it either. */}
              <Route
                path="/trace/config/describe"
                element={hasTrace && aiEnabled && canManagePanels ? <PanelFromText /> : <Navigate to={traceHome} replace />}
              />
              <Route path="/trace/config/:setupId" element={hasTrace && canManagePanels ? <ConfigPage /> : <Navigate to={traceHome} replace />} />
              {/* Product Knowledge — AI features, gated on the account's AI licence
                  (like Describe-a-panel) so a bookmarked URL can't reach them either. */}
              <Route path="/trace/knowledge" element={hasTrace && aiEnabled ? <ProductsLibrary isAdmin={isAdmin} /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/knowledge/:productId" element={hasTrace && aiEnabled ? <ProductKnowledge isAdmin={isAdmin} /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/prompts" element={hasTrace && aiEnabled ? <AiPrompts isAdmin={isAdmin} /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/build/:setupId" element={hasTrace && aiEnabled ? <TraceBuilder /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/build/:setupId/import" element={hasTrace && aiEnabled ? <DocImport /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/build/:setupId/import/:importId" element={hasTrace && aiEnabled ? <DocImport /> : <Navigate to={traceHome} replace />} />
              <Route path="/trace/matrix/:setupId" element={hasTrace ? <MatrixPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/trace/baselines/:setupId" element={hasTrace ? <TraceBaselinesPage aiEnabled={aiEnabled} canManagePanels={canManagePanels} /> : <Navigate to={homeRoute} replace />} />
              {/* Instant reports. Read-only by nature — they own no filters and can trigger
                  no build — so viewing needs only the module, exactly like a matrix. */}
              <Route path="/trace/report/:setupId" element={hasTrace ? <ReportPanel module="trace" /> : <Navigate to={homeRoute} replace />} />

              {/* ---- Training module ------------------------------------------------ */}
              {/* Training panel URLs are unchanged from v3.9.0 — only the module home at
                  /training is new, so no training bookmark ever breaks. */}
              {/* A VIEWER's front door is their own to-do, not the panel list. A training
                  panel is a compliance report about people — written for the person
                  auditing, not the person being audited — and a read-only licence is
                  exactly the trainee case. They keep every quiz route below; only the
                  panel list and the panel itself are traded for the screen that tells
                  them what they owe. Fails open: an unreadable permission string leaves
                  `isViewer` false and the dashboard exactly as it was. */}
              <Route
                path="/training"
                element={!hasTraining
                  ? <Navigate to={homeRoute} replace />
                  : isViewer
                    ? <MyTraining isHome />
                    : <Dashboard module="training" onLogout={handleLogout} aiEnabled={aiEnabled} canManagePanels={canManagePanels} isAdmin={isAdmin} version={releaseNotes?.releases?.[0]?.version} account={user?.account_name} />}
              />
              {/* Training Traceability — a panel is this app's dashboard, so authoring it
                  needs the same Orcanos dashboard-add right (Critical Note #23). Viewing
                  is open to anyone the panel is shared with. */}
              {/* Quizzes. Authoring is ADMIN-only (QA manager = Orcanos administrator) and
                  fails closed; My to-do and sitting a quiz are open to everyone,
                  viewers included — that is the trainee half. The server re-checks and
                  403s regardless of what is rendered here.
                  These static segments must stay ABOVE /training/:setupId; React Router
                  ranks static over dynamic, so "quizzes" is never read as a panel id. */}
              <Route path="/training/quizzes" element={hasTraining && isAdmin ? <QuizList /> : <Navigate to={trainingHome} replace />} />
              <Route path="/training/quizzes/new" element={hasTraining && isAdmin ? <QuizBuilder /> : <Navigate to={trainingHome} replace />} />
              <Route path="/training/quizzes/:quizId" element={hasTraining && isAdmin ? <QuizBuilder /> : <Navigate to={trainingHome} replace />} />
              <Route path="/training/my" element={hasTraining ? <MyTraining isHome={isViewer} /> : <Navigate to={homeRoute} replace />} />
              <Route path="/training/attempt/:attemptId" element={hasTraining ? <TakeQuiz /> : <Navigate to={homeRoute} replace />} />

              {/* People — the roster over every training panel's snapshot. `people` is a
                  static segment and MUST stay above /training/:setupId, or a People panel id
                  would be read as a training panel id and 400 on the wrong guard. A viewer
                  goes to their own to-do: this is the QA manager's view of everyone else. */}
              <Route path="/training/people/:setupId" element={hasTraining ? (isViewer ? <Navigate to="/training/my" replace /> : <PeoplePanel />) : <Navigate to={homeRoute} replace />} />
              <Route path="/training/people/:setupId/edit" element={hasTraining && canManagePanels ? <PeopleConfig /> : <Navigate to={trainingHome} replace />} />

              {/* Instant reports. `report` is a static segment and MUST stay above
                  /training/:setupId for the same reason `people` does — below it, a report
                  id would be read as a training panel id and 400 on the wrong guard. A
                  viewer goes to their own to-do: these are the QA manager's summaries. */}
              <Route path="/training/report/:setupId" element={hasTraining ? (isViewer ? <Navigate to="/training/my" replace /> : <ReportPanel module="training" />) : <Navigate to={homeRoute} replace />} />

              <Route path="/training/new" element={hasTraining && canManagePanels ? <TrainingConfig /> : <Navigate to={trainingHome} replace />} />
              <Route path="/training/:setupId/edit" element={hasTraining && canManagePanels ? <TrainingConfig /> : <Navigate to={trainingHome} replace />} />
              {/* Same rule as the module home: a viewer is sent to their to-do rather
                  than into the compliance grid. A deep link from an email lands there
                  too, which is the point — it is where the answer to "what do I owe?"
                  actually is. */}
              <Route path="/training/:setupId" element={hasTraining ? (isViewer ? <Navigate to="/training/my" replace /> : <TrainingPanel isAdmin={isAdmin} />) : <Navigate to={homeRoute} replace />} />

              {/* ---- Doc Review module ----------------------------------------------- */}
              {/* OPT-IN (allow_review defaults to 0). Every route needs only the module
                  licence here; the server re-checks it and every action's own rule. */}
              <Route path="/review" element={hasReview ? <ReviewHome canManagePanels={canManagePanels} /> : <Navigate to={homeRoute} replace />} />
              <Route path="/review/open" element={hasReview ? <ReviewOpenPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/review/item/:itemId" element={hasReview ? <ReviewItemPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/review/r/:reviewId" element={hasReview ? <ReviewDocPage /> : <Navigate to={homeRoute} replace />} />

              {/* ---- DHF module ------------------------------------------------------ */}
              {/* No licence of its own: `dhf` is in modules[] exactly when `trace` is. The server
                  re-checks it and the panel's privacy on every route. */}
              <Route path="/dhf" element={hasDhf ? <DhfHome canManagePanels={canManagePanels} hasTrace={hasTrace} /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId" element={hasDhf ? <DhfPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/doc/:docId" element={hasDhf ? <DhfDocumentPage /> : <Navigate to={homeRoute} replace />} />
              {/* Document Variants (2026-10-27): forks of a DHF document, kept here; Orcanos only on Merge. */}
              <Route path="/dhf/:setupId/doc/:docId/variants" element={hasDhf ? <DhfVariantsPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/doc/:docId/variants/compare" element={hasDhf ? <DhfVariantComparePage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/variant/:vid" element={hasDhf ? <DhfVariantPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/variant/:vid/pull" element={hasDhf ? <DhfVariantPullPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/variant/:vid/merge" element={hasDhf ? <DhfVariantMergePage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/dhf/:setupId/baselines"element={hasDhf ? <DhfBaselinesPage aiEnabled={aiEnabled} canManagePanels={canManagePanels} /> : <Navigate to={homeRoute} replace />} />

              {/* ---- Risk module ----------------------------------------------------- */}
              {/* OPT-IN (allow_risk defaults to 0). The server re-checks the licence on
                  every route; panel create/edit also needs canManagePanels there. */}
              <Route path="/risk" element={hasRisk ? <RiskHome canManagePanels={canManagePanels} hasTrace={hasTrace} /> : <Navigate to={homeRoute} replace />} />
              <Route path="/risk/:setupId/r/:itemId" element={hasRisk ? <RiskDetailPage /> : <Navigate to={homeRoute} replace />} />
              <Route path="/risk/:setupId/import" element={hasRisk && canManagePanels ? <ImportUpload /> : <Navigate to="/risk" replace />} />
              <Route path="/risk/import/:batchId" element={hasRisk && canManagePanels ? <ImportPage /> : <Navigate to="/risk" replace />} />
              <Route path="/risk/:setupId/studio" element={hasRisk ? <StudioPage canManagePanels={canManagePanels} /> : <Navigate to={homeRoute} replace />} />
              <Route path="/risk/:setupId/reports" element={hasRisk ? <ReportsPage /> : <Navigate to={homeRoute} replace />} />
              {/* OPT-IN (allow_integrations defaults to 0). The builder is admin-only on the
                  server too (D2); a non-admin who opens it is told so. */}
              <Route path="/integrations" element={hasIntegrations ? <IntegrationsHome /> : <Navigate to={homeRoute} replace />} />
              <Route path="/integrations/:id/edit" element={hasIntegrations && isAdmin ? <IntegrationBuilder /> : <Navigate to="/integrations" replace />} />
              <Route path="/integrations/:id/log" element={hasIntegrations ? <IntegrationRunLog /> : <Navigate to={homeRoute} replace />} />
              <Route path="/risk/:setupId/baselines" element={hasRisk ? <BaselinesPage aiEnabled={aiEnabled} canManagePanels={canManagePanels} /> : <Navigate to={homeRoute} replace />} />

              {/* ---- BOM module ------------------------------------------------------ */}
              {/* OPT-IN (allow_bom defaults to 0). Read-only browsing, so every route needs
                  only the module licence; settings writes are admin-gated on the server.
                  The view is a URL segment so the nav can highlight it and it deep-links. */}
              <Route path="/bom" element={<Navigate to={hasBom ? bomHome : homeRoute} replace />} />
              <Route
                path="/bom/:view"
                element={hasHardwareBom
                  ? <BomViewer
                      userId={user?.user_id}
                      onSessionExpired={handleBomSessionExpired}
                    />
                  : <Navigate to={hasSbom ? '/bom/sw-releases' : homeRoute} replace />}
              />
              {/* Product structures (2026-10-28) — the hierarchy kept in the BOM Viewer, beside the
                  hardware tree. Hardware BOM licence; the page itself says when its switch is off. */}
              {['ps-dashboard', 'ps-structures', 'ps-import', 'ps-parts', 'ps-baselines', 'ps-branches'].map((v) => (
                <Route key={v} path={`/bom/${v}`}
                  element={hasHardwareBom ? <ProductStructures view={v} onSessionExpired={handleBomSessionExpired} />
                    : <Navigate to={hasBom ? bomHome : homeRoute} replace />} />
              ))}
              {/* Software BOM (v4.30.0) — its own licence and its own page beside the hardware
                  viewer. Static segments, so they win over /bom/:view. */}
              {['sw-dashboard', 'sw-releases', 'sw-components', 'sw-vulns', 'sw-studio', 'sw-import'].map((v) => (
                <Route key={v} path={`/bom/${v}`}
                  element={hasSbom ? <SbomViewer view={v} onSessionExpired={handleBomSessionExpired} />
                    : <Navigate to={hasBom ? bomHome : homeRoute} replace />} />
              ))}
              {/* SBOM Dashboard (v4.32.0): one release's full view and its baselines. */}
              {[['sw-dashboard', '/bom/sw-dashboard/:releaseId'], ['sw-baselines', '/bom/sw-dashboard/:releaseId/baselines']].map(([v, path]) => (
                <Route key={path} path={path}
                  element={hasSbom ? <SbomViewer view={v} onSessionExpired={handleBomSessionExpired} />
                    : <Navigate to={hasBom ? bomHome : homeRoute} replace />} />
              ))}
              <Route path="/bom/sw-import/:importId"
                element={hasSbom ? <SbomViewer view="sw-import" onSessionExpired={handleBomSessionExpired} />
                  : <Navigate to={hasBom ? bomHome : homeRoute} replace />} />

              {/* ---- Admin ----------------------------------------------------------- */}
              {/* Tenant settings, opened from the user menu's Admin group. Admin-only here;
                  the server re-checks every save. */}
              {/* The user's OWN settings. Ungated — everyone has preferences, and the page
                  hides a section whose feature the account does not have rather than
                  refusing the route. Not to be confused with /settings, which is the
                  admin's tenant-wide screen. */}
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/settings" element={isAdmin ? <AdminSettings hasBom={hasHardwareBom} hasSbom={hasSbom} account={user?.account_name} /> : <Navigate to={homeRoute} replace />} />

              {/* ---- Legacy routes (pre-v3.23.0) ------------------------------------ */}
              {/* Permanent redirects, not duplicates: an old bookmark or a link in someone's
                  email must keep working, and it must land on the canonical URL so the
                  module switcher can tell where it is. `*` carries the rest of the path. */}
              <Route path="/dashboard" element={<Navigate to="/trace/dashboard" replace />} />
              <Route path="/config/*" element={<LegacyRedirect to="/trace/config" />} />
              <Route path="/config" element={<Navigate to="/trace/config" replace />} />
              <Route path="/knowledge/*" element={<LegacyRedirect to="/trace/knowledge" />} />
              <Route path="/knowledge" element={<Navigate to="/trace/knowledge" replace />} />
              <Route path="/prompts" element={<Navigate to="/trace/prompts" replace />} />
              <Route path="/build/*" element={<LegacyRedirect to="/trace/build" />} />
              <Route path="/matrix/*" element={<LegacyRedirect to="/trace/matrix" />} />

              <Route path="/login" element={<AfterLogin homeRoute={homeRoute} ready={Array.isArray(user?.modules)} who={identityOf(user)} />} />
              <Route path="*" element={<Navigate to={homeRoute} replace />} />
            </>
          )}
        </Routes>
        </div>

        {isAuthenticated && collab.enabled && collabPerson && (
          <ChatDock
            person={collabPerson}
            me={collab.me}
            thread={collab.threads[collabPerson.user_id] || []}
            meta={collab.threadMeta[collabPerson.user_id]}
            pendingContext={collab.openWith?.pendingContext}
            retentionDays={collab.retentionDays}
            editWindowSeconds={collab.editWindowSeconds}
            onClose={collab.close}
            onSend={collab.send}
            onLoadEarlier={collab.loadEarlier}
            onSearch={collab.searchThread}
            onJumpToMessage={collab.jumpToMessage}
            onJumpToLatest={collab.jumpToLatest}
            onAnchorShown={collab.clearAnchor}
            onEdit={collab.editMessage}
            onDelete={collab.deleteMessage}
          />
        )}
        </div>
        </div>
      </div>
    </Router>
  );
}

export default App;
